/***
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 **/

//IWA Client only for now, web is possible

import { SDKRawEvent } from "../event-helper/event-helper";
import { EventExchangeBase, SDKReceivedRawRequest } from "./event-exchange.base";
import { Logger } from "../logger";
interface ParsedArgs {
   url: string;
   extensionId?: string;
   portKey?: string;
   [key: string]: string | undefined; // For any additional parameters
 }
 
export class EventExchangeLongLivedMessageClient extends EventExchangeBase {
   private ports: Map<string, chrome.runtime.Port> = new Map();
   private callbacks: Map<string, Function> = new Map();
   private keepAliveIntervals: Map<string, any> = new Map();//TODO: need to change tsconfig for strong type
   private KEEP_ALIVE_INTERVAL_MS = 20000; // 20 seconds
   
   constructor(
      allowedPeerAddresss: Array<string>|null,
      getTrustOriginList: (string) => Promise<Array<string>>,
      isTrustSyncMessage?: (SDKRawEvent) => boolean
   ) {
      let hasChromeRuntime = chrome && chrome.runtime;
      Logger.info("LivedMessageClient, chrome run time:" + (hasChromeRuntime ? "has": "no") + " chrome.runtime");
      super(allowedPeerAddresss, getTrustOriginList, isTrustSyncMessage);
      if (chrome?.runtime?.connect && window.launchQueue?.setConsumer) {// request to manual close existing client not controled by SDk to allow SDK to work.
         window.launchQueue.setConsumer((launchParams) => {
            Logger.info("detect launchParams for SDK, connect to target port" + JSON.stringify(launchParams));
            // if launch handler get an event, and the url contains the extensionid and connectKey, then connect to this right away.
            let params = this.parseIsolatedAppUrl(launchParams.targetURL)
            // get both from URL args in the launchParams;
            let peerAddress = params.extensionId;
            let key = params.portKey;
            if(!peerAddress || !key) {
               Logger.error("Invalid launchParams for SDK, must contain extensionId and portKey");
               return;
            }
            // Connect to the peerAddress with the key as port name
            Logger.debug(`Connecting to peerAddress: ${peerAddress} with key: ${key}`);
            let port = chrome.runtime.connect(peerAddress, { name: key });
            // The port's onDisconnect event is fired if the extension/app does not exist or receive connection.
            port.onDisconnect.addListener((port) => {
               Logger.debug(`Port disconnected since receiving end does not exist.`);
               const key = port.name;
               this.ports.delete(key);
               this._emitDisconnect(key, "Connection to extension lost");
            });
            this.ports.set(key, port);
            // Start sending keep-alive messages from client side
            this.startKeepAliveInterval(key, port);
            this.addListenerForPort(port, peerAddress);
          });
      }
   }



   private parseIsolatedAppUrl(urlString: string): ParsedArgs {
      try {
         // Create URL object
         const url = new URL(urlString);
         
         // Get the 'url' parameter and decode it
         const encodedInnerUrl = url.searchParams.get('url') || '';
         const decodedInnerUrl = decodeURIComponent(encodedInnerUrl);
         
         // Parse the inner URL (web+horizon://...)
         const innerUrl = new URL(decodedInnerUrl);
         
         // Get all search params from the inner URL
         const params: ParsedArgs = {
            url: decodedInnerUrl
         };
         
         // Parse all search params from the inner URL
         innerUrl.searchParams.forEach((value, key) => {
            params[key] = decodeURIComponent(value);
         });
      
         return params;
      } catch (error) {
         Logger.error('Error parsing URL');
         Logger.exception(error);
         return { url: urlString };
      }
   }
   public disconnectPort = (portId: string, reason?: string) => {
      const port = this.ports.get(portId);
      if (port) {
         Logger.debug(`Disconnet port ${port.name}, portId : ${portId} since ${reason || "manual disconnection"}`);
         port.disconnect();
         this.ports.delete(portId);
         
         // Clear keep-alive interval
         this.clearKeepAliveInterval(portId);
         
         this._emitDisconnect(portId, reason || "manual disconnection");
      }
   };

   private startKeepAliveInterval(portId: string, port: any) {
      // Clear any existing interval first
      this.clearKeepAliveInterval(portId);
      
      // Set new interval
      const intervalId = setInterval(() => {
         if (this.ports.has(portId)) {
            // Send a simple keep-alive message
            port.postMessage({
               msgId: `keepalive-${Date.now()}`,
               message: { type: "KEEP_ALIVE", data: { timestamp: Date.now(), source: "client" } } as SDKRawEvent,
               isKeepAlive: true
            });
            Logger.debug(`Sent keep-alive message to port ${portId}`); // Changed from info to debug
         } else {
            this.clearKeepAliveInterval(portId);
         }
      }, this.KEEP_ALIVE_INTERVAL_MS);
      
      this.keepAliveIntervals.set(portId, intervalId);
      Logger.info(`Started keep-alive interval for port ${portId} (${this.KEEP_ALIVE_INTERVAL_MS}ms)`); // Added info log for interval start
   }

   private clearKeepAliveInterval(portId: string) {
      const interval = this.keepAliveIntervals.get(portId);
      if (interval) {
         clearInterval(interval);
         this.keepAliveIntervals.delete(portId);
         Logger.info(`Cleared keep-alive interval for port ${portId}`); // Added info log for interval clear
      }
   }

   public sendMessage = (peerAddress: string, data: SDKRawEvent, portId: string|undefined = undefined): Promise<SDKRawEvent> => {
      let port: any;
      const key = portId ? portId : peerAddress;
      return new Promise((resolve, reject) => {
         try {
            if (!this.ports.has(key)) {
               port = chrome.runtime.connect(peerAddress, { name: key });
               // The port's onDisconnect event is fired if the extension/app does not exist or receive connection.
               port.onDisconnect.addListener((port) => {
                  Logger.debug(`Port disconnected since receiving end does not exist.`);
                  const disconnectedPortId = port.name || key;
                  this.ports.delete(key);
                  this._emitDisconnect(disconnectedPortId, "Connection to extension lost");
                  reject(new Error("Port disconnected"));
               });
               this.ports.set(key, port);
               // Start sending keep-alive messages
               this.startKeepAliveInterval(key, port);
               this.addListenerForPort(port, peerAddress);
            } else {
               port = this.ports.get(key);
               Logger.debug(`Use existed port ${port.name}`);
            }

            const msgId = Math.random().toString(36);
            this.callbacks.set(msgId, (response: SDKRawEvent) => {
               resolve(response);
            });

            port.postMessage({ msgId: msgId, message: data });
            Logger.trace(
               `Sent data ${JSON.stringify({ msgId: msgId, message: data })} from port ${JSON.stringify(port)}`
            );
         } catch (e) {
            this.ports.delete(key);
            Logger.debug(`Failed to open port with app ${peerAddress}. Error in chrome.runtime.connect`);
            reject(e);
         }
      });
   };

   private addListenerForPort = (port: any, msgSenderOrigin: string): boolean => {
      port.onMessage.addListener((data, port) => {
         const message = data.message;
         const msgId = data.msgId;
         
         // Handle keep-alive messages with improved logging
         if (data.isKeepAlive) {
            Logger.debug(`Received keep-alive message from port ${port.name}`); // Changed from info to debug
            return;
         }
         
         Logger.trace(`Receive data ${JSON.stringify(data)} from port ${JSON.stringify(port)}`);
         
         if (data.isResponse) {
            const callback = this.callbacks.get(msgId);
            if (typeof callback === "function") {
               try {
                  callback(message);
               } catch (e) {
                  Logger.error(`Error in callback for message ${msgId}: ${e && e.message ? e.message : e}`);
               }
               Logger.debug(`Message ${msgId} is resolved.`);
            }
            this.callbacks.delete(msgId);
         } else {
            //const msgSenderOrigin = this.getOrigin(port.sender.url);

            if (!msgSenderOrigin) {
               return false;
            }
            Logger.info("adding listener for port on address: "+msgSenderOrigin)
            
            if(!msgSenderOrigin.startsWith("chrome-extension://")){
               msgSenderOrigin = "chrome-extension://" + msgSenderOrigin;
            }
            this.getAllowedPeerAddress(msgSenderOrigin, message).then((matchedPeerAddress) => {
               if (!matchedPeerAddress) {
                  Logger.warning("ignore message from " + msgSenderOrigin);
                  return false;
               }

               // double check sender in code again
               const request: SDKReceivedRawRequest = {
                  request: message as SDKRawEvent,
                  sendResponse: (response: SDKRawEvent) => {
                     port.postMessage({ isResponse: true, msgId: msgId, message: response });
                  },
                  peerAddress: matchedPeerAddress
               };
               this.message$.next(request);
            });
         }
      });
      return true;
   };
}
